import { last } from 'lodash';
import {
  AutoGeneratedChildrenCategoryId,
  ElementCategoryConversionFactorRecord,
  ElementCategoryID,
  IElementCategory,
  IElementCategoryPropertySelectionByProductIdFn,
  MainElementCategoryConversionFactorRecord,
  ProductCategoryId,
  ServiceCategoryId,
  SystemCategoryId,
  autoGeneratedChildrenCategoryIdsSet,
  isProductCategoryId,
  productCategoryIds,
  serviceCategoryIds,
  serviceCategoryIdsSet,
  systemCategoryIds,
  systemCategoryIdsSet,
} from '../models/element-categories.model';
import {
  ElementPropertyName,
  ElementPropertySource,
  IElementProperty,
  PropertyResolvedCountRecord,
} from '../models/element-property.model';
import {
  DEFAULT_QUANTITY_EXPRESSION,
  DEFAULT_QUANTITY_EXPRESSIONS,
  ElementQuantityExpressionRecord,
  ElementQuantityRecord,
  PIECES_QUANTITY_NAME,
} from '../models/element-quantities.model';
import {
  CreatedElement,
  ElementKind,
  ElementKindMap,
  IElement,
  IElementID,
  IProductElement,
  ProductCategoryElement,
  OneOfChildElements,
  OneOfElements,
  OneOfListElements,
  OneOfParentElements,
} from '../models/project.model';
import shallowEqual, {
  asArray,
  cloneRemoveItems,
  isDefined,
  isOneOf,
} from './array.helpers';
import {
  elementCategories,
  getElementCategoryById,
} from '../templates/categories';
import {
  createElementFromPartial,
  isFactoryElement,
  isFactoryProductElement,
  isOneOfParentFactoryElements,
} from './element-factory.helpers';
import { isGenerated } from './element.helpers';
import { collectCategoryPropertyTemplateDriverNames } from './conditional-nodes.helpers';
import { createElementProperty } from './element-property-factory.helpers';
import {
  applyElementPropertiesOfSource,
  propertyChangeRecordToArray,
  setPropertyCountsFromRecord,
  setElementProperties,
  getElementProperties,
  hasCount,
  isElementExpressionProperty,
  isElementPropertySourceCategory,
  isElementSelectProperty,
  isPropertyCountClearable,
  isSelectPropertyOptionValue,
  setPropertyCount,
  type PropertyChange,
} from './element-property.helpers';
import { cloneReplaceAt } from './array.helpers';
import {
  createElementQuantityRecord,
  getDefaultProductCountExpressionFromParent,
  getElementQuantityRecord,
  isElementQuantityExpressionProperty,
  selectElementQuantity,
} from './element-quantity.helpers';
import {
  filterObject,
  getKeys,
  isPartialEqual,
  mapFilterRecord,
  omit,
  omitUndefined,
  pick,
  replaceProperties,
} from './object.helpers';
import { isExpressionValue } from './expression-factory.helpers';
import {
  PropertyValidationError,
  SELECT_COUNT_NOT_IN_OPTIONS_MESSAGE,
} from '../validation/validation.models';
import {
  isBuildingVersionElement,
  isElement,
  isProductElement,
} from './recursive-element.helpers';
import {
  buildDeterministicRecipeNestedChildId,
  clearRecipe,
  isRecipeScopedElementId,
} from './recipe.helpers';
import {
  getConversionFactorValue,
  SupportedConversionUnits,
} from './conversion-factors.helpers';
import { Replace } from '../models/type-helpers.model';
import { enrichElementStructure } from './project.helpers';
import { RegenerateIds } from './id.helpers';
import {
  IFactoryElement,
  IFactoryProductElement,
  OneOfChildFactoryElements,
  OneOfFactoryElements,
} from '../models/factory-element.model';
import { required } from './function.helpers';
import {
  filterConditionalNodes,
  filterConditionalProperties,
  getValueFromConditionalNodes,
} from './conditional-nodes.helpers';
import { ProductID } from '../models/product.model';
import { genericProductsLookup } from '../generic-products';
import { Recipe } from '../models/recipe.model';
import {
  conditionalReusedContentFactoryElement,
  reusedContentProductId,
} from '../templates/categories/reused-content-property.model';
import { getProductId } from './product.helpers';
import { createExpression } from './expression-factory.helpers';
import { guards } from './typeguards.helpers';
import { createId } from './id.helpers';
import {
  getElementCategoryId,
  type UnknownCategoryId,
} from './element-category-id.helpers';
import { getCategoryPropertyValueRecord } from './category-property-value-record.helpers';
import { logOutsideTest } from './debug.helpers';

export type { UnknownCategoryId } from './element-category-id.helpers';
export {
  getElementCategoryId,
  isElementCategoryID,
} from './element-category-id.helpers';
export { getCategoryPropertyValueRecord };

type OneOfTypesWithCategoryId = OneOfListElements | IElementProperty | Recipe;

export type PathRecord = Record<IElementID, OneOfParentElements[]>;

/**
 * Whether {@link IElement.category_id} may be changed after the element exists.
 * Category is chosen at creation; EPD generic-product settings use a separate in-dialog picker.
 */
export const canChangeElementCategory = (
  _element: Pick<IElement, 'id'>,
): boolean => false;

/**
 * Set element category ID on an element and update properties belonging to the category
 * @param element
 * @param id
 * @param keepRecipe If true, will not clear the recipe. Currently only used to migrate categories and keep the element intact
 * @returns
 */
export const applyElementCategory = (
  element: IElement,
  id: ElementCategoryID | string | undefined,
  keepRecipe = false,
): IElement => {
  const prevId = element.category_id;
  const category_id = getElementCategoryId(id);

  // If category is the same as the original, return the element without modifying it
  if (prevId === category_id) {
    return element;
  }

  // Clear any recipe belonging to previous category
  element = keepRecipe ? element : clearRecipe(element);

  // Clear any optional properties that are no longer available
  element = removeObsoleteOptionalProperties(element, category_id);

  // Get quantities from category and keep existing values if they exist
  const quantity = getQuantityFromCategory(element, category_id);

  return selectElementQuantity(
    applyElementCategoryProperties({
      ...element,
      quantity,
      category_id,
      recipe_id: undefined,
    }),
    'auto',
  );
};

/**
 * Remove optional properties from element that are no longer available in new category
 * @param element
 * @param newCategoryId
 * @returns
 */
const removeObsoleteOptionalProperties = (
  element: IElement,
  newCategoryId: ElementCategoryID | undefined,
): IElement => {
  if (newCategoryId === getElementCategoryId(element)) {
    return element;
  }
  const properties = getElementProperties(element);

  // Names of currently available optional properties
  const currentNames = getOptionalProperties(element).map(({ name }) => name);
  // Names of available optional properties in new category
  const newNames = getOptionalProperties(element, newCategoryId).map(
    ({ name }) => name,
  );

  // Remove properties that derive from previous category but are not available in new category
  const updatedProperties = properties.filter(({ name }) => {
    if (currentNames.includes(name)) {
      return newNames.includes(name);
    }
    return true;
  });

  // If the properties are the same, return the original element
  return properties.length !== updatedProperties.length
    ? { ...element, properties: updatedProperties }
    : element;
};

/**
 * Create an element with an applied category. Will also apply category to child elements recursively.
 * Can't be done directly in factory due to circular reference issues. TODO: Make this behavoiur default in createElementFromPartial
 * @param element
 * @returns
 */
export const createElementWithCategory = <
  T extends OneOfFactoryElements | Partial<IFactoryElement>,
  K extends (T['kind'] extends keyof ElementKindMap
    ? T['kind']
    : ElementKind.Element),
>(
  element: T,
  regenerateIds?: RegenerateIds,
): CreatedElement<K> => {
  const factoryEl = {
    kind: ElementKind.Element, // Default kind
    ...element,
  } as OneOfFactoryElements;

  // Recursively create create child elements (so full tree is created)
  if (isOneOfParentFactoryElements(factoryEl)) {
    factoryEl.elements = factoryEl.elements?.map((child) =>
      createElementWithCategory(child, regenerateIds),
    );
  }

  // IElement should get category applied, the other elements should just be tre
  if (isFactoryElement(factoryEl)) {
    const category = getElementCategory(factoryEl);
    const fallbackName = category?.name;

    return applyElementCategory(
      createElementFromPartial(
        omit({ fallbackName, ...factoryEl }, 'category_id'),
        regenerateIds,
      ),
      category?.id,
    ) as CreatedElement<K>;
  }

  return createElementFromPartial(
    factoryEl,
    regenerateIds,
  ) as CreatedElement<K>;
};

/**
 * Apply a IElementCategoryPropertyValueRecord to an element.
 * Will recalculate which properties are visible based on the new values
 * and then fill them with the correct values (recursively).
 * Since sometimes you must change a value in a property for another property to show
 * @param element
 * @param category_id
 * @param record
 * @returns
 */
export const setElementCategoryPropertyValueRecord = (
  element: IElement,
  record: PropertyResolvedCountRecord | undefined,
  category_id: ElementCategoryID | undefined = element.category_id,
): IElement => {
  let updatedElement = enrichElementStructure(
    applyElementCategory(element, category_id),
  );

  // Only need to apply properties if we have a category and a record
  if (category_id && Object.keys(record ?? {}).length) {
    // Somewhat ugly hack to make sure the properties are set
    // Since some properties are added depending on other properties we need to iterate a few times (in theory 2 should be enough).
    // In theory this could be executed until no changes are applied but to make sure we never cause infite loops we do it in for loop instead of a while(true).
    for (let i = 0; i < 3; i++) {
      if (record) {
        const updatedPropsElement = enrichElementStructure(
          setPropertyCountsFromRecord(updatedElement, record, true),
        );

        // If no changes were made we can break the loop and return the updated element
        if (updatedPropsElement === updatedElement) {
          return updatedElement;
        }
        updatedElement = updatedPropsElement;
      }
    }
  }
  return updatedElement;
};

/**
 * Reorders {@link IElement.properties} so category-backed rows follow the category template
 * order (as in {@link getElementPropertiesFromCategory}), then optional category rows (see
 * {@link getOptionalProperties}), then any remaining properties (e.g. recipe or custom) in their
 * current relative order.
 *
 * @param element - Element whose `properties` should be reordered
 */
export function orderElementPropertiesLikeCategoryTemplate(
  element: IElement,
): IElementProperty[] {
  const merged = getElementProperties(element);
  if (getElementCategoryId(element) === ElementCategoryID.None) {
    return merged;
  }

  const templateOrdered = getElementPropertiesFromCategory(element);
  if (templateOrdered.length === 0) {
    return merged;
  }

  const byName = new Map(merged.map((p) => [p.name, p]));
  const used = new Set<string>();
  const ordered = templateOrdered.map((tp) => {
    const fromMerged = byName.get(tp.name);
    const pick = fromMerged ?? tp;
    used.add(pick.name);
    return pick;
  });

  // Optional rows are defined on the category separately from `properties`;
  // skip any name already in the main template so we do not duplicate.
  const mainNames = new Set(templateOrdered.map((p) => p.name));
  const optionalOrdered: IElementProperty[] = [];
  // Walk optional template order; only emit rows that actually exist on the element
  // (persisted instance), not factory-only definitions.
  for (const tp of getOptionalProperties(element)) {
    if (!tp.name || mainNames.has(tp.name)) continue;
    const fromMerged = byName.get(tp.name);
    // Skip if the user has not added this optional yet, or it was already placed in the main-template pass.
    if (!fromMerged || used.has(fromMerged.name)) continue;
    used.add(fromMerged.name);
    optionalOrdered.push(fromMerged);
  }

  // Remaining properties (e.g. recipe, custom) keep their relative order after category rows
  const leftovers = merged.filter((p) => !used.has(p.name));
  return [...ordered, ...optionalOrdered, ...leftovers];
}

/**
 * Category-sourced properties on {@link element} in the same sequence as
 * {@link orderElementPropertiesLikeCategoryTemplate} (main template, optional template, then
 * remaining category rows on the element). Cheaper than ordering the full merged list and
 * filtering when callers only need category-backed rows (eg sidebar property lists).
 *
 * @param element - Element with {@link IElement.properties} to read
 */
export function getCategorySourcedPropertiesInTemplateOrder(
  element: IElement,
): IElementProperty[] {
  const merged = getElementProperties(element);
  if (getElementCategoryId(element) === ElementCategoryID.None) {
    return merged.filter(isElementPropertySourceCategory);
  }

  const templateOrdered = getElementPropertiesFromCategory(element);
  if (templateOrdered.length === 0) {
    return merged.filter(isElementPropertySourceCategory);
  }

  const byName = new Map(merged.map((p) => [p.name, p]));
  const used = new Set<string>();
  const out: IElementProperty[] = [];

  for (const tp of templateOrdered) {
    const pick = byName.get(tp.name) ?? tp;
    used.add(pick.name);
    if (isElementPropertySourceCategory(pick)) out.push(pick);
  }

  const mainNames = new Set(templateOrdered.map((p) => p.name));
  for (const tp of getOptionalProperties(element)) {
    if (!tp.name || mainNames.has(tp.name)) continue;
    const fromMerged = byName.get(tp.name);
    if (!fromMerged || used.has(fromMerged.name)) continue;
    used.add(fromMerged.name);
    if (isElementPropertySourceCategory(fromMerged)) out.push(fromMerged);
  }

  for (const p of merged) {
    if (used.has(p.name)) continue;
    if (isElementPropertySourceCategory(p)) out.push(p);
  }

  return out;
}

/**
 * Apply (remove/add) latest properties from category to element based on current properties.
 * @param element Element to update, note that correct category_id must be set
 * @returns If element is changed it will return a new element else it will return the original element
 */
export const applyElementCategoryProperties = <T extends OneOfElements>(
  element: T,
): T => {
  if (!isElement(element)) {
    return element;
  }

  const elementWithProperties = deriveCategoryPropertyRows(element);

  const elementWithUpdatedProperties = updateQuantityPropertiesFromCategory(
    elementWithProperties,
  );

  const withChildren = applyCategoryChildElements(
    elementWithUpdatedProperties,
  ) as IElement;

  if (!isElement(withChildren)) {
    return withChildren as T;
  }

  return reorderToCategoryTemplate(withChildren) as T;
};

/**
 * Lightweight counterpart of {@link applyElementCategoryProperties} that only re-derives
 * the set of category-backed properties (add/remove based on conditional rules) and
 * reorders them into the category template order. Skips quantity sync, child-element
 * sync, fallback inheritance, data table sync, and auto-quantity — those run later in
 * {@link enrichElementStructure}.
 *
 * Intended for pipeline updates where a count change on a category-driving Select / Switch
 * may unlock or hide dependent properties (e.g. `heat_source` appears once
 * `heating_type=heat_pump`). Re-deriving inline lets subsequent property writes in the
 * same batch find the new rows without waiting for a full enrich pass — the missing piece
 * that made the second write a no-op before.
 *
 * @param element Element whose category-driven properties should be refreshed
 * @returns Same reference when nothing changes; otherwise a new element with updated `properties`
 */
export const applyConditionalCategoryProperties = <T extends OneOfElements>(
  element: T,
): T => {
  if (!isElement(element)) return element;
  const withDerived = deriveCategoryPropertyRows(element);
  return reorderToCategoryTemplate(withDerived) as T;
};

/**
 * True when changing a property's value would add or remove category-backed property
 * rows (via {@link applyConditionalCategoryProperties}). Product-only selectors such as
 * `amount_scrap_based`, `quality`, or `cop` return false; root selects like `material` or
 * `heating_type` return true when they show or hide dependent rows.
 *
 * @param element - Element with category applied
 * @param propertyName - Property being edited
 * @param count - New count / value for the property row
 */
export const propertyEditReshapesCategoryPropertyTree = (
  element: IElement,
  propertyName: string,
  count: PropertyChange['count'],
): boolean => {
  if (getElementCategoryId(element) === ElementCategoryID.None) return false;

  const props = getElementProperties(element);
  const propIndex = props.findIndex((p) => p.name === propertyName);
  if (propIndex === -1) return false;

  const beforeNames = new Set(
    getElementProperties(applyConditionalCategoryProperties(element)).map(
      (p) => p.name,
    ),
  );

  const prop = props[propIndex];
  if (!prop) return false;
  // Undefined count means "clear the row". Reinforcement steel cannot be
  // cleared, so a missing numeric override must not be applied as a full clear.
  if (count === undefined && !isPropertyCountClearable(prop)) return false;
  const updatedProp = setPropertyCount(prop, count, 'count', {
    skipValidation: true,
  });
  const updatedProps = cloneReplaceAt(
    props,
    propIndex,
    updatedProp as IElementProperty,
  );
  const afterNames = new Set(
    getElementProperties(
      applyConditionalCategoryProperties({
        ...element,
        properties: updatedProps,
      }),
    ).map((p) => p.name),
  );

  if (beforeNames.size !== afterNames.size) return true;
  for (const name of beforeNames) {
    if (!afterNames.has(name)) return true;
  }
  for (const name of afterNames) {
    if (!beforeNames.has(name)) return true;
  }
  return false;
};

/**
 * True when editing this property should drop a stale {@link ProjectChangeType.ElementCategoryEnrich}
 * on the same element (no replacement enrich in the batch). Combines runtime property-tree
 * diff with template driver names for cases like `material` where products change but row
 * names stay the same.
 */
export const propertyEditInvalidatesPriorCategoryEnrich = (
  element: IElement,
  propertyName: string,
  count: PropertyChange['count'],
): boolean => {
  if (getElementCategoryId(element) === ElementCategoryID.None) return false;

  if (propertyEditReshapesCategoryPropertyTree(element, propertyName, count)) {
    return true;
  }

  const category = getElementCategory(element);
  if (!category?.properties?.length) return false;

  return collectCategoryPropertyTemplateDriverNames(category.properties).has(
    propertyName,
  );
};

/**
 * Replaces {@link IElement.properties} category-source rows with the freshly resolved
 * template rows (counts reused from removed rows where possible). Validation is skipped
 * because stored counts may temporarily fall outside freshly resolved options (e.g. a
 * proposal flipping `material` to Steel while keeping `form: Profile` from Aluminium);
 * the user-input boundary still validates.
 */
const deriveCategoryPropertyRows = (element: IElement): IElement =>
  applyElementPropertiesOfSource(
    element,
    ElementPropertySource.Category,
    getElementPropertiesFromCategory(element),
    { skipValidation: true },
  );

/** Reorder {@link IElement.properties} into category template order, returning the same reference when unchanged. */
const reorderToCategoryTemplate = (element: IElement): IElement => {
  const orderedProps = orderElementPropertiesLikeCategoryTemplate(element);
  const prevProps = getElementProperties(element);
  const orderUnchanged =
    prevProps.length === orderedProps.length &&
    prevProps.every((p, i) => p === orderedProps[i]);
  if (orderUnchanged) return element;
  return replaceProperties(element, { properties: orderedProps }) as IElement;
};

/**
 * Whether category-driven quantity metadata (e.g. displayUnit) changed while keys stayed the same.
 */
const categoryQuantityTemplateMetadataChanged = (
  quantity: ElementQuantityRecord,
  categoryQuantity: ElementQuantityExpressionRecord,
): boolean =>
  getKeys(categoryQuantity).some((name) => {
    const prev = quantity[name as keyof typeof quantity];
    const next = categoryQuantity[name as keyof typeof categoryQuantity];
    if (!prev && !next) return false;
    if (!prev || !next) return true;
    if (
      !isElementQuantityExpressionProperty(prev) ||
      !isElementQuantityExpressionProperty(next)
    ) {
      return prev !== next;
    }
    return (
      prev.displayUnit !== next.displayUnit ||
      prev.displayName !== next.displayName ||
      prev.readonly !== next.readonly ||
      prev.hidden !== next.hidden ||
      prev.inheritFallback !== next.inheritFallback
    );
  });

/**
 * Apply latest quantity properties from category to element
 * @param element Element to update, note that correct category_id must be set
 * @returns If element is changed it will return a new element else it will return the original element
 */
const updateQuantityPropertiesFromCategory = <T extends OneOfElements>(
  element: T,
): T => {
  if (!isElement(element)) {
    return element;
  }
  const quantity = getElementQuantityRecord(element);
  const categoryQuantity = getQuantityFromCategory(element);
  // Quantities not belonging to a category (only used for testing currently so can probably be deprecated)
  const nonCategoryProperties = omitUndefined(
    filterObject(quantity, (p) => !p?.category_id),
  );
  const newQuantity = { ...nonCategoryProperties, ...categoryQuantity };
  const toRemove = getKeys(quantity).filter((p) => !newQuantity[p]);

  // Without this we get some weird overwrite issues...
  const keysUnchanged = shallowEqual(getKeys(quantity), getKeys(newQuantity));
  const recipeScopedNeedsFallbackSync =
    isRecipeScopedElementId(element.id) &&
    getKeys(categoryQuantity).some((name) => {
      const prev = quantity[name as keyof typeof quantity];
      const next = categoryQuantity[name as keyof typeof categoryQuantity];
      const prevFallback = prev?.fallbackCount;
      const nextFallback = next?.fallbackCount;
      return (
        isExpressionValue(prevFallback) &&
        prevFallback.expression === '0' &&
        isExpressionValue(nextFallback) &&
        !!nextFallback.expression &&
        nextFallback.expression !== '0'
      );
    });
  const templateMetadataChanged = categoryQuantityTemplateMetadataChanged(
    quantity,
    categoryQuantity,
  );

  if (
    keysUnchanged &&
    !recipeScopedNeedsFallbackSync &&
    !templateMetadataChanged
  ) {
    return element;
  }

  return replaceProperties(element, {
    quantity: replaceProperties(omit(quantity, ...toRemove), newQuantity),
  } as Partial<typeof element>) as T;
};

/**
 * Get element properties from category. Use category set on element unless you provide an alternative id
 * @param element
 * @param id Alternative id if you want to get properties from a different category than the one in the element
 * @returns
 */
export const getElementPropertiesFromCategory = (
  element: OneOfElements,
  category_id: ElementCategoryID = getElementCategoryId(element),
): IElementProperty[] => {
  const category = getElementCategory(category_id);

  if (!category || !isElement(element)) {
    return [];
  }
  const staticConditionalProperties = category.properties ?? [];

  const properties = filterConditionalProperties(
    element,
    ...staticConditionalProperties,
  );

  // Reuse IDs from filterConditionalProperties; only generate new UUIDs for genuinely new properties.
  // skipValidation: enrich must not throw on stored counts that are no longer in the freshly
  // resolved options (e.g. proposal still carries `form: Profile` after material flips
  // Aluminium → Steel). Validation runs at user-input boundary instead.
  return properties.map((p) =>
    createElementProperty({ ...p, category_id }, undefined, false, {
      skipValidation: true,
    }),
  );

  // TODO: Streamline this with reused properties...

  // Add properties that should be generated by each category
  // return addMutualProperties(properties, category).map((p) =>
  //   createElementProperty({ ...p, category_id }),
  // );
};

/**
 * Get optional properties from category
 * @param element
 * @param categoryId Optional category id if you want to get optional properties from a different category than the one in the element
 * @returns
 */
export const getOptionalProperties = (
  element: OneOfElements,
  categoryId: ElementCategoryID = getElementCategoryId(element),
) => {
  // Content coverage on a version is a hidden property edited by the slider,
  // so it must not appear in the add-property menu.
  if (isBuildingVersionElement(element)) return [];

  const category = getElementCategory(categoryId);
  if (!category || !isElement(element)) {
    return [];
  }
  return filterConditionalProperties(
    element,
    ...(category.optionalProperties ?? []),
  ).map((p) =>
    createElementProperty({ ...p, optional: true }, undefined, undefined, {
      skipValidation: true,
    }),
  );
};

/**
 * Get element category from element or category_id
 * @param elementOrId
 * @returns
 */
export const getElementCategory = (
  elementOrId?:
    | OneOfTypesWithCategoryId
    | UnknownCategoryId
    | OneOfFactoryElements
    | undefined,
): Readonly<IElementCategory> | undefined => {
  const id = getElementCategoryId(elementOrId);
  return id ? getElementCategoryById(id) : undefined;
};

export const isElementOfCategory = <CAT extends ElementCategoryID>(
  element: OneOfElements | undefined,
  ...categories: [CAT, ...CAT[]]
): element is IElement & { category_id: CAT } => {
  return isOneOf(categories, getElementCategoryId(element));
};

/**
 * Get quantity properties from element category and keep existing values if they exist
 * @param element
 * @param overrideCategory Pass category if you wanna override the category of the element (when applying new one)
 * @param path The path of elements leading to the element
 * @returns
 */
const getQuantityFromCategory = (
  element: OneOfElements,
  overrideCategory?: ElementCategoryID,
): ElementQuantityExpressionRecord => {
  const category = getElementCategory(overrideCategory ?? element);
  if (category && isElement(element)) {
    const quantityPropertiesFn = category.getQuantityProperties;
    const quantity = getElementQuantityRecord(element);
    if (quantityPropertiesFn) {
      // Create a new record with updated fallbackCounts
      const record = createElementQuantityRecord(quantityPropertiesFn(element));

      const mergeQuantityFromCategory = (): ElementQuantityExpressionRecord =>
        mapFilterRecord(record, (pr) => {
          if (!pr) {
            return undefined;
          }

          // Apply the new category id
          pr.category_id = category.id;
          const existing = quantity[pr.name];

          // Keep count, fallbackCount and updated_at if they have a value
          if (existing) {
            const defaultExpressions = [
              DEFAULT_QUANTITY_EXPRESSION,
              DEFAULT_QUANTITY_EXPRESSIONS[pr.name],
            ];

            // Existing properties (fallback on new properties if it doesn't have an existing value)
            const {
              id,
              fallbackCount = pr.fallbackCount,
              count = pr.count,
              updated_at = pr.updated_at,
            } = existing;
            const newFallbackExp = pr.fallbackCount?.expression;

            // Should we use new or existing fallback?
            // Frozen {@link ProjectChangeType.ElementRecipeApply} snapshots often store fallbackCount "0";
            // category templates carry the real inheritance formula for recipe-scoped children.
            const existingFallbackIsFrozenZero =
              isRecipeScopedElementId(element.id) &&
              existing.fallbackCount?.expression === '0';

            const useNewFallback =
              !defaultExpressions.includes(newFallbackExp) &&
              (newFallbackExp !== existing.fallbackCount?.expression ||
                existingFallbackIsFrozenZero);

            return {
              ...pr,
              id,
              updated_at,
              count,
              fallbackCount: useNewFallback ? pr.fallbackCount : fallbackCount,
              inheritFallback: existing.inheritFallback ?? pr.inheritFallback,
            };
          }
          return pr;
        });

      const merged = mergeQuantityFromCategory();

      // Keep a visible custom `pieces` property as the expression source until
      // a migration absorbs it into the quantity row.
      const hasVisiblePiecesProperty = getElementProperties(element).some(
        (property) =>
          property.name === PIECES_QUANTITY_NAME && !property.hidden,
      );
      if (hasVisiblePiecesProperty && !quantity[PIECES_QUANTITY_NAME]) {
        const { [PIECES_QUANTITY_NAME]: _omitted, ...withoutPieces } = merged;
        return withoutPieces;
      }

      // Hidden custom `pieces` still carries the user expression until/after absorb.
      const piecesProperty = getElementProperties(element).find(
        (property) => property.name === PIECES_QUANTITY_NAME,
      );
      const piecesQuantity = merged[PIECES_QUANTITY_NAME];
      if (
        piecesProperty &&
        piecesQuantity &&
        isElementExpressionProperty(piecesProperty) &&
        hasCount(piecesProperty) &&
        !hasCount(piecesQuantity)
      ) {
        return {
          ...merged,
          [PIECES_QUANTITY_NAME]: {
            ...piecesQuantity,
            count: piecesProperty.count,
          },
        };
      }
      return merged;
    }
  }
  return {};
};

/**
 * Allow for product ids to be passed as child elements
 * @param e
 * @returns
 */
const toCategoryChildFactoryElement = (
  child: IFactoryProductElement | ProductID,
  parent: IElement,
): IFactoryProductElement => {
  if (typeof child === 'string') {
    const product = required(
      genericProductsLookup[child],
      `Product ${child} not found`,
    );

    return {
      kind: ElementKind.Product,
      unit: product.unit,
      count: getDefaultProductCountExpressionFromParent(parent),
      product_id: child,
    };
  }
  return child;
};

/**
 * Get factory elements from element category based on current element properties
 * @param element
 * @param overrideCategory
 * @returns
 */
const getFactoryElementsFromCategory = (
  element: OneOfElements,
  overrideCategory?: IElementCategory,
): IFactoryProductElement[] | undefined => {
  if (isElement(element)) {
    const category = overrideCategory ?? getElementCategory(element);
    const getChildElementsFn = category?.getChildElements;

    if (getChildElementsFn) {
      const childElementsResult = getChildElementsFn(element);
      // `undefined` from getChildElements means "could not resolve children
      // right now" (transient invalid state) — propagate so the caller can
      // skip enrichment instead of stripping existing children.
      if (childElementsResult === undefined) return undefined;

      const factoryElementsOrProductIds = filterConditionalNodes(
        element,
        ...asArray(childElementsResult),
      );
      const factoryElements = factoryElementsOrProductIds.map((child) =>
        toCategoryChildFactoryElement(child, element),
      );

      return applyReusedContentElement(element, factoryElements);
    }
  }
  return [];
};

const applyReusedContentElement = (
  element: IElement,
  factoryElements: IFactoryProductElement[],
): IFactoryProductElement[] => {
  const hasReusedContent = factoryElements.some(
    (childOrId) => getProductId(childOrId) === reusedContentProductId,
  );

  // Reused content already applied
  if (hasReusedContent) {
    return factoryElements;
  }

  const reusedContentElement = getValueFromConditionalNodes(
    element,
    [conditionalReusedContentFactoryElement],
    false,
  );

  if (reusedContentElement) {
    return [
      ...factoryElements.map((e) => {
        const count = createExpression(e.count);
        return {
          ...e,
          count: {
            ...count,
            expression: `(${count.expression}) * (1 - ${ElementPropertyName.ReusedContent})`,
          },
        };
      }),
      reusedContentElement,
    ];
  }
  return factoryElements;
};

/**
 * True when the category template product targets the same catalog row as an existing child
 * (same product_id or EPD linked via generic_product_id).
 * Used so merge keeps user data (e.g. climate / cost overrides) during enrichment and duplicate flows.
 */
const isSameCategoryProductSlot = (
  templateProduct: { product_id: ProductID },
  existingProduct: {
    product_id: ProductID;
    generic_product_id: ProductID | undefined;
  },
): boolean =>
  templateProduct.product_id === existingProduct.product_id ||
  (existingProduct.generic_product_id !== undefined &&
    templateProduct.product_id === existingProduct.generic_product_id);

/**
 * Get child elements from element category based on current element properties.
 * @param element - Element whose category drives child slots
 * @param overrideCategory - Optional category override
 * @returns Factory-ready children; when an index matches an existing child of the same product slot, preserves overrides and EPD linkage
 */
export const getCategoryChildElements = (
  element: OneOfElements,
  overrideCategory?: IElementCategory,
): OneOfChildFactoryElements[] | undefined => {
  if (!isElement(element)) {
    return [];
  }

  const factoryElements = getFactoryElementsFromCategory(
    element,
    overrideCategory,
  );

  // `undefined` means the category template could not resolve children for
  // this element's current (transient) state — caller should leave existing
  // children alone.
  if (factoryElements === undefined) return undefined;

  return factoryElements
    .map((e) =>
      // Create new elements based on category
      isFactoryProductElement(e)
        ? createElementFromPartial(e)
        : createElementWithCategory(e),
    )
    .map((e, i) => {
      const existingElement = element.elements[i];
      if (!existingElement || existingElement.kind !== e.kind) {
        return e;
      }

      if (isElement(e)) {
        // e should always be IProductElement so this should never happen...
        return e;
      }

      if (!isProductElement(e) || !isProductElement(existingElement)) {
        return { ...e, id: existingElement.id };
      }

      const existingCount = existingElement.count;

      // If a new count is created reuse the resolved count (results live on proposal.resultsRecord, not element)
      const count =
        existingCount && e.count.resolved === 0
          ? { ...existingCount, expression: e.count.expression }
          : e.count;

      const merged = { ...e, id: existingElement.id, count };

      if (!isSameCategoryProductSlot(e, existingElement)) {
        return merged;
      }

      // Same slot: keep the chosen EPD and overrides. Whether an empty count
      // hides the row stays on the template. Copying hide_product_without_count
      // from the previous child hid the concrete EPD after a type change.
      return {
        ...merged,
        product_id: existingElement.product_id,
        generic_product_id: existingElement.generic_product_id,
        productOverrides: existingElement.productOverrides,
        name: existingElement.name,
        unit: existingElement.unit,
      };
    });
};

/**
 * Empty-count visibility belongs to the category template. A reused EPD must
 * not keep `hide_product_without_count` from an earlier slot, or the selector
 * stays hidden after the type changes.
 *
 * @param product - Existing product row being reused
 * @param template - Category child for this slot
 */
const withTemplateProductVisibility = (
  product: IProductElement,
  template: IProductElement,
): IProductElement => {
  if (template.hide_product_without_count) {
    return product.hide_product_without_count
      ? product
      : { ...product, hide_product_without_count: true };
  }
  if (!product.hide_product_without_count) return product;
  const { hide_product_without_count: _hidden, ...visible } = product;
  return visible;
};

/**
 * Apply child elements from category to element.
 * If no category chid elements are found, the original element is returned
 * @param element
 * @returns The modified element or the original element if no changes were made
 */
/** When the parent is recipe-scoped, product slots use {@link buildDeterministicRecipeNestedChildId}. */
const withRecipeScopedProductChildId = (
  parent: IElement,
  child: OneOfChildElements,
  index: number,
): OneOfChildElements => {
  if (!isRecipeScopedElementId(parent.id)) return child;
  const id = buildDeterministicRecipeNestedChildId(parent.id, child, index);
  return child.id === id ? child : { ...child, id };
};

export const applyCategoryChildElements = (element: IElement): IElement => {
  const categoryChildren = getCategoryChildElements(element);

  // Category template signalled "cannot resolve children now" (transient
  // invalid Select state). Skip enrichment entirely so existing generated
  // children survive until the element reaches a valid state again.
  if (categoryChildren === undefined) return element;

  const currentProductElements = element.elements.filter(isProductElement);
  const categoryChildProductElements =
    categoryChildren.filter(isProductElement);

  if (categoryChildren.length) {
    // Reuse existing product elements if found; each existing element can only be used once to avoid duplicate element updates
    const usedElementIds = new Set<string>();
    const productElements = categoryChildProductElements.map((child, index) => {
      const existing = currentProductElements.find(
        (el) =>
          el.product_id === child.product_id &&
          el.count.expression === child.count.expression,
      );
      const matchingEpdProductElements = currentProductElements.filter(
        (el) => el.generic_product_id === child.product_id,
      );
      // For elements (like heating) that might have multiple elements mapped to the same generic product, try to use the one at the right index
      const epdProductElement = isOneOf(
        matchingEpdProductElements,
        currentProductElements[index],
      )
        ? currentProductElements[index]
        : matchingEpdProductElements[0];

      const epdProductElementWithReusedContentCount =
        epdProductElement?.generic_product_id === child.product_id
          ? { ...epdProductElement, count: child.count }
          : epdProductElement;

      // Prefer exact match, then EPD match; only reuse if that element id is not already used for another slot
      if (existing && !usedElementIds.has(existing.id)) {
        usedElementIds.add(existing.id);
        return withTemplateProductVisibility(existing, child);
      }
      if (
        epdProductElementWithReusedContentCount &&
        !usedElementIds.has(epdProductElementWithReusedContentCount.id)
      ) {
        usedElementIds.add(epdProductElementWithReusedContentCount.id);
        return withTemplateProductVisibility(
          epdProductElementWithReusedContentCount,
          child,
        );
      }
      return withRecipeScopedProductChildId(element, child, index);
    });

    // Deduplicate: when category has more slots than element children, the same existing element
    // can be matched for multiple slots (via existing/epdProductElement) despite usedElementIds,
    // or the fallback child can reuse an id from element.elements[i]. Clone duplicates with new ids.
    const seenIds = new Set<string>();
    const deduplicated = productElements.map((el, index) => {
      if (seenIds.has(el.id)) {
        return withRecipeScopedProductChildId(
          element,
          { ...el, id: createId({}) },
          index,
        );
      }
      seenIds.add(el.id);
      return withRecipeScopedProductChildId(element, el, index);
    });

    // Mutate if there are changes
    if (!shallowEqual(deduplicated, element.elements)) {
      return { ...element, elements: deduplicated };
    }
  }
  // Cleanup previously generated elements when the element has a category but no child template
  // (e.g. user switched category from Concrete to Wall). Skip when the element has no category at all
  // since those parents are allowed to host user-added product children.
  else if (getElementCategoryId(element)) {
    const elements = element.elements.filter((el) => !isGenerated(el));

    if (elements.length !== element.elements.length) {
      return { ...element, elements };
    }
  }
  return element;
};

/**
 * Get the hierarchy of categories from a path filtering out any non-category ids
 * @param path
 * @returns
 */
export const getCategoryPath = (
  path: OneOfParentElements[] = [],
): ElementCategoryID[] => path.map(getElementCategoryId).filter((id) => !!id);

/**
 * Get the defined category id closest to the element
 * @param path
 * @returns
 */
export const getClosestElementCategoryId = (
  path: OneOfParentElements[],
): ElementCategoryID | undefined => last(getCategoryPath(path));

export const getSubCategoryConversionFactors = (
  record: MainElementCategoryConversionFactorRecord,
): ElementCategoryConversionFactorRecord[] => {
  return Object.entries(record).map(([, factors]) => factors);
};

export const getSumOfCategoryConversionFactors = (
  record: ElementCategoryConversionFactorRecord,
  factor: SupportedConversionUnits,
): number => {
  return Object.entries(record).reduce((sum, [, factors]) => {
    return sum + getConversionFactorValue(factors, factor);
  }, 0);
};

export const isServiceCategory = (
  id?: ElementCategoryID | ServiceCategoryId | 'none',
): id is ServiceCategoryId =>
  typeof id === 'string' && serviceCategoryIdsSet.has(id);

type IServiceCategoryElement = Replace<
  IElement,
  { category_id: ServiceCategoryId }
>;

export const isServiceCategoryElement = (
  element: unknown,
): element is IServiceCategoryElement =>
  isElement(element) && isServiceCategory(getElementCategoryId(element));

export const isProductCategory = (
  id?: ElementCategoryID | ProductCategoryId | 'none',
): id is ProductCategoryId => typeof id === 'string' && isProductCategoryId(id);

export const isProductCategoryElement = (
  element: unknown,
): element is ProductCategoryElement =>
  isElement(element) && isProductCategory(getElementCategoryId(element));

export const isSystemCategory = (
  id?: ElementCategoryID | SystemCategoryId | 'none',
): id is SystemCategoryId =>
  guards.is.defined(id) && systemCategoryIdsSet.has(id);

export enum CategoryType {
  System = 'system',
  Product = 'product',
  Service = 'service',
  Other = 'other',
}

export const getCategoryType = (
  id?:
    | ElementCategoryID
    | SystemCategoryId
    | ProductCategoryId
    | ServiceCategoryId
    | 'none',
): CategoryType => {
  if (isSystemCategory(id)) return CategoryType.System;
  if (isProductCategory(id)) return CategoryType.Product;
  if (isServiceCategory(id)) return CategoryType.Service;
  return CategoryType.Other;
};

export const getCategoryIdsByType = (
  type: CategoryType,
): ElementCategoryID[] => {
  if (type === CategoryType.System) return [...systemCategoryIds];
  if (type === CategoryType.Product) return [...productCategoryIds];
  if (type === CategoryType.Service) return [...serviceCategoryIds];
  return [];
};

export const getCategoryIdsByTypes = (
  ...types: CategoryType[]
): ElementCategoryID[] => {
  return types.flatMap((type) => getCategoryIdsByType(type));
};

export const isAutoGeneratedChildrenCategory = (
  id?: ElementCategoryID | AutoGeneratedChildrenCategoryId | 'none',
): id is AutoGeneratedChildrenCategoryId =>
  typeof id === 'string' && autoGeneratedChildrenCategoryIdsSet.has(id);

type ISystemCategoryElement = Replace<
  IElement,
  { category_id: SystemCategoryId }
>;
export const isSystemCategoryElement = (
  element: unknown,
): element is ISystemCategoryElement =>
  isElement(element) && isSystemCategory(getElementCategoryId(element));

export const getElementProductCategory = (
  productId: ProductID,
): ProductCategoryId => {
  for (const category of elementCategories) {
    const { id, getElementPropertySelectionByProductId } = category;
    if (
      getElementPropertySelectionByProductId?.(productId) &&
      isProductCategoryId(id)
    ) {
      return id;
    }
  }
  return ElementCategoryID.OtherProduct;
};

type ElementCategoryAndSelections = {
  category_id: ElementCategoryID;
  selections: ReturnType<IElementCategoryPropertySelectionByProductIdFn>;
};

/**
 * Find the category + property selections that reproduce one of the given
 * product ids, or `undefined` when no category can select any of them.
 *
 * Unlike {@link getElementCategoryAndSelectionsByProductId} this does NOT fall
 * back to OtherProduct: a missing result is the authoritative signal that the
 * product cannot be materialized into a proper element. Use this when the
 * absence of a real match must be distinguishable (e.g. building the list of
 * products selectable through product categories).
 */
export const findElementCategoryAndSelectionsByProductId = (
  ...productIds: (string | undefined)[]
): ElementCategoryAndSelections | undefined => {
  const definedProductIds = productIds.filter(isDefined);
  for (const category of elementCategories) {
    const { id, getElementPropertySelectionByProductId } = category;

    if (!getElementPropertySelectionByProductId) {
      continue;
    }

    for (const productId of definedProductIds) {
      const selections = getElementPropertySelectionByProductId?.(productId);
      // Get which selection that will lead to the specific product id
      if (selections) {
        return { category_id: id, selections };
      }
    }
  }
  return undefined;
};

/**
 * Get the category and selections (values of properties) that will result in a
 * certain product id. Falls back to OtherProduct with empty selections when no
 * category matches — callers that need to detect "not selectable" should use
 * {@link findElementCategoryAndSelectionsByProductId} instead.
 * @param productIds
 * @returns
 */
export const getElementCategoryAndSelectionsByProductId = (
  ...productIds: (string | undefined)[]
): ElementCategoryAndSelections | undefined =>
  findElementCategoryAndSelectionsByProductId(...productIds) ?? {
    category_id: ElementCategoryID.OtherProduct,
    selections: {},
  };

/**
 * True when {@link change} can be written onto {@link element} right now.
 *
 * Select values outside the current options return false so a later selection
 * can reveal them. Callers skip a value that never becomes valid.
 *
 * @param element - Element whose current properties decide applicability
 * @param change - One pending category property selection
 */
const isApplicableCategoryPropertySelection = (
  element: IElement,
  change: PropertyChange,
): boolean => {
  if (!change.name) return false;

  const property = getElementProperties(element).find(
    (item) => item.name === change.name,
  );
  if (!property) return false;
  if (!isElementSelectProperty(property)) return true;
  return isSelectPropertyOptionValue(property, change.count);
};

/**
 * True when applying a select wrote a value the current options reject.
 *
 * @param error - Failure from {@link setElementProperties}
 */
const isSelectOptionRejected = (error: unknown): boolean =>
  error instanceof PropertyValidationError &&
  error.message === SELECT_COUNT_NOT_IN_OPTIONS_MESSAGE;

/**
 * Apply property selections to an element.
 *
 * Applies one property at a time so conditional rows are added before a later
 * selection chooses them. A select value that is not in the current options
 * waits until another selection reveals it. Selections that never match a
 * property, or never become a valid option — leftover keys from another
 * category — are skipped. Overlapping keys still apply.
 *
 * Used by product/category pickers — not by recipe apply flows.
 *
 * @param element - Target element
 * @param selections - Category property values to apply
 * @returns Element with applicable selections applied
 */
export const applyElementCategoryPropertySelections = (
  element: IElement,
  selections: ReturnType<IElementCategoryPropertySelectionByProductIdFn> = {},
) => {
  let pending = propertyChangeRecordToArray(selections);
  if (!getElementProperties(element).length || !pending.length) {
    return element;
  }

  const appliedNames: string[] = [];

  // Match a property that exists now and can take this value. Applying one
  // can reveal the next conditional option. Stop when nothing left can be
  // applied — those keys belong to another category.
  while (pending.length) {
    const change = pending.find((candidate) =>
      isApplicableCategoryPropertySelection(element, candidate),
    );
    const name = change?.name;
    if (!change || !name) break;

    try {
      element = applyElementCategoryProperties(
        setElementProperties(element, element, change),
      );
    } catch (error) {
      // Options rejected the value after the pre-check. Drop it and continue.
      if (isSelectOptionRejected(error)) {
        pending = cloneRemoveItems(pending, change);
        continue;
      }
      logOutsideTest.error(
        'Error applying property selections',
        selections,
        pending,
        error,
      );
      throw error;
    }

    pending = cloneRemoveItems(pending, change);
    appliedNames.push(name);
  }

  const updatedProps = getCategoryPropertyValueRecord(element);
  const appliedSelections = pick(selections, ...appliedNames);
  if (!isPartialEqual(updatedProps, appliedSelections)) {
    throw new Error('Property selections do not match');
  }

  return element;
};
